// Asbestos Floorplan Intelligence — editable canvas (simplified).
// SVG overlay over the original scan (falls back to the standardised base if no
// original was uploaded): zoom / pan / fit-to-view, layer toggles, draw
// rectangle / circle / polygon with snap-to-zone-vertex. Coordinates are stored
// as structured JSON (image-pixel space) per the data model. Zones autosave.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    MousePointer2, Hand, Square, Circle as CircleIcon, PenTool, ZoomIn, ZoomOut,
    Maximize2, Trash2, Layers, Loader2, Check, FileText, Expand, Shrink,
} from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiError } from "@/lib/api";
import {
    STATUS_FILL, STATUS_STROKE, STATUS_LABEL, STATUS_GLYPH, VERTEX_SNAP_PX,
    zoneCentroid, r0,
} from "@/pages/asbestos/canvas/constants";
import { ResizeHandles, ZoneShape } from "@/pages/asbestos/canvas/ZoneShapes";
import { ZoneDetailPanel } from "@/pages/asbestos/canvas/ZoneDetailPanel";

export default function FloorCanvas({ floor, canEdit, documents = [], onFloorUpdate, buildingId, clientId, onDocsRefresh, initialZoneId }) {
    const navigate = useNavigate();
    const location = useLocation();
    const W = floor.width || 1000;
    const H = floor.height || 700;
    const wrapRef = useRef(null);
    const svgRef = useRef(null);

    const [zones, setZones] = useState(floor.zones || []);
    const [tool, setTool] = useState("select");        // select | pan | rect | circle | polygon
    const [view, setView] = useState({ scale: 1, tx: 0, ty: 0 });
    const [layers, setLayers] = useState({ base: true, asbestos: true, annotations: true, guides: false });
    const [newStatus, setNewStatus] = useState("red");
    const [selectedId, setSelectedId] = useState(null);
    const [hoveredId, setHoveredId] = useState(null);
    const [tip, setTip] = useState(null);              // {x, y} in wrap-relative px
    const [draft, setDraft] = useState(null);           // in-progress rect/circle
    const [poly, setPoly] = useState(null);             // {pts:[{x,y}], cursor:{x,y}}
    const [cursor, setCursor] = useState(null);
    const [saveState, setSaveState] = useState("idle"); // idle | saving | saved
    const [fullscreen, setFullscreen] = useState(false);
    const [drag, setDrag] = useState(null);             // {id, kind, index} active resize handle
    const [moving, setMoving] = useState(false);        // dragging a whole zone
    const moveRef = useRef(null);                        // {id, start, orig, shape, moved}
    const panRef = useRef(null);
    const saveTimer = useRef(null);
    const pendingZones = useRef(null);   // last zones payload awaiting save (for flush-on-unmount)

    useEffect(() => {
        setZones(floor.zones || []);
        setSelectedId(initialZoneId && (floor.zones || []).some((z) => z.id === initialZoneId) ? initialZoneId : null);
        setDraft(null); setPoly(null);
    }, [floor.id, initialZoneId]);

    // ---- fit-to-view ----
    const fit = useCallback(() => {
        const el = wrapRef.current;
        if (!el) return;
        const cw = el.clientWidth, ch = el.clientHeight;
        const scale = Math.min(cw / W, ch / H) * 0.96;
        setView({ scale, tx: (cw - W * scale) / 2, ty: (ch - H * scale) / 2 });
    }, [W, H]);
    useEffect(() => { fit(); }, [fit, floor.id]);

    // ---- coordinate helpers ----
    const toImg = useCallback((clientX, clientY) => {
        const r = svgRef.current.getBoundingClientRect();
        return { x: (clientX - r.left - view.tx) / view.scale, y: (clientY - r.top - view.ty) / view.scale };
    }, [view]);

    const allVertices = useMemo(() => {
        const v = [];
        for (const z of zones) {
            if (z.shape === "rectangle") { const [x1, y1, x2, y2] = z.coords; v.push({ x: x1, y: y1 }, { x: x2, y: y1 }, { x: x2, y: y2 }, { x: x1, y: y2 }); }
            else if (z.shape === "circle") { const [cx, cy] = z.coords; v.push({ x: cx, y: cy }); }
            else if (z.shape === "polygon") { for (let i = 0; i < z.coords.length; i += 2) v.push({ x: z.coords[i], y: z.coords[i + 1] }); }
        }
        return v;
    }, [zones]);

    // Always snap to the nearest existing zone vertex within range — keeps drawn
    // shapes tidy without exposing a separate "snapping" setting to the user.
    const snap = useCallback((p) => {
        const { x, y } = p;
        let best = null, bd = VERTEX_SNAP_PX;
        for (const vtx of allVertices) {
            const d = Math.hypot(vtx.x - x, vtx.y - y);
            if (d < bd) { bd = d; best = vtx; }
        }
        if (best) return { x: best.x, y: best.y, snapped: true };
        return { x, y, snapped: false };
    }, [allVertices]);

    // ---- persistence ----
    const persist = useCallback((next) => {
        pendingZones.current = next;
        setSaveState("saving");
        clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(async () => {
            const payload = pendingZones.current;
            pendingZones.current = null;
            try {
                await api.put(`/asbestos/floors/${floor.id}/zones`, { zones: payload });
                setSaveState("saved");
                onFloorUpdate?.(floor.id, { zones: payload });   // keep parent in sync
                setTimeout(() => setSaveState("idle"), 1500);
            } catch (e) {
                setSaveState("idle");
                toast.error(formatApiError(e.response?.data?.detail) || "Could not save zones");
            }
        }, 400);
    }, [floor.id, onFloorUpdate]);

    // Flush any pending zone save immediately when unmounting / switching floors so a
    // freshly-drawn shape is never lost on a screen flip or navigation.
    useEffect(() => {
        const fid = floor.id;
        return () => {
            clearTimeout(saveTimer.current);
            const payload = pendingZones.current;
            pendingZones.current = null;
            if (payload) {
                api.put(`/asbestos/floors/${fid}/zones`, { zones: payload }).catch(() => {});
                onFloorUpdate?.(fid, { zones: payload });
            }
        };
    }, [floor.id, onFloorUpdate]);

    const commitZones = useCallback((next) => { setZones(next); persist(next); }, [persist]);

    const addZone = useCallback((shape, coords) => {
        const z = { id: `z_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, shape, coords, status: newStatus };
        const next = [...zones, z];
        commitZones(next);
        setSelectedId(z.id);
    }, [zones, newStatus, commitZones]);

    const deleteSelected = useCallback(() => {
        if (!selectedId) return;
        commitZones(zones.filter((z) => z.id !== selectedId));
        setSelectedId(null);
    }, [selectedId, zones, commitZones]);

    const updateZone = useCallback((id, patch) => {
        commitZones(zones.map((z) => z.id === id ? { ...z, ...patch } : z));
    }, [zones, commitZones]);

    // For server-authoritative sub-resources (photos / inspection history) that already
    // persisted themselves via a dedicated endpoint — update local state only, no re-save.
    const updateZoneLocal = useCallback((id, patch) => {
        setZones((zs) => {
            const next = zs.map((z) => z.id === id ? { ...z, ...patch } : z);
            onFloorUpdate?.(floor.id, { zones: next });
            return next;
        });
    }, [floor.id, onFloorUpdate]);

    // Live-resize a zone via a handle drag (local update; persisted on pointer-up).
    const applyResize = useCallback((d, p) => {
        setZones((zs) => zs.map((z) => {
            if (z.id !== d.id) return z;
            const c = [...z.coords];
            if (z.shape === "rectangle") {
                if (d.kind === "corner") {
                    if (d.index === 0) { c[0] = p.x; c[1] = p.y; }
                    else if (d.index === 1) { c[2] = p.x; c[1] = p.y; }
                    else if (d.index === 2) { c[2] = p.x; c[3] = p.y; }
                    else { c[0] = p.x; c[3] = p.y; }
                }
            } else if (z.shape === "circle") {
                c[2] = Math.max(2, Math.hypot(p.x - c[0], p.y - c[1]));
            } else if (z.shape === "polygon") {
                c[d.index * 2] = p.x; c[d.index * 2 + 1] = p.y;
            }
            return { ...z, coords: c.map((n) => Math.round(n)) };
        }));
    }, []);

    const selectedZone = zones.find((z) => z.id === selectedId) || null;
    const hoveredZone = zones.find((z) => z.id === hoveredId) || null;

    // Begin moving a whole zone (only with the Select tool, when editable).
    function startMove(e, z) {
        if (tool !== "select" || !canEdit) return;     // viewers still select via onClick
        e.stopPropagation();
        setSelectedId(z.id);
        const p = snap(toImg(e.clientX, e.clientY));
        moveRef.current = { id: z.id, start: p, orig: [...z.coords], shape: z.shape, moved: false };
        setMoving(true);
    }
    function movedCoords(m, dx, dy) {
        if (m.shape === "circle") return [Math.round(m.orig[0] + dx), Math.round(m.orig[1] + dy), Math.round(m.orig[2])];
        return m.orig.map((n, i) => Math.round(i % 2 === 0 ? n + dx : n + dy));
    }

    // ---- pointer interactions ----
    function onWheel(e) {
        e.preventDefault();
        const r = svgRef.current.getBoundingClientRect();
        const mx = e.clientX - r.left, my = e.clientY - r.top;
        const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
        setView((v) => {
            const ns = Math.min(8, Math.max(0.08, v.scale * factor));
            const k = ns / v.scale;
            return { scale: ns, tx: mx - (mx - v.tx) * k, ty: my - (my - v.ty) * k };
        });
    }

    function onPointerDown(e) {
        if (e.button === 1 || tool === "pan" || e.spaceKey) {
            panRef.current = { sx: e.clientX, sy: e.clientY, tx: view.tx, ty: view.ty };
            return;
        }
        const p = snap(toImg(e.clientX, e.clientY));
        if (tool === "rect" || tool === "circle") {
            setDraft({ shape: tool, start: p, end: p });
        } else if (tool === "polygon") {
            setPoly((pp) => {
                if (!pp) return { pts: [p], cursor: p };
                // close if clicking near the first point
                const f = pp.pts[0];
                if (pp.pts.length >= 3 && Math.hypot(f.x - p.x, f.y - p.y) < VERTEX_SNAP_PX) {
                    const coords = pp.pts.flatMap((pt) => [pt.x, pt.y]);
                    addZone("polygon", coords);
                    return null;
                }
                return { pts: [...pp.pts, p], cursor: p };
            });
        } else if (tool === "select") {
            setSelectedId(null);
        }
    }

    function onPointerMove(e) {
        if (panRef.current) {
            const { sx, sy, tx, ty } = panRef.current;
            setView((v) => ({ ...v, tx: tx + (e.clientX - sx), ty: ty + (e.clientY - sy) }));
            return;
        }
        const raw = toImg(e.clientX, e.clientY);
        const p = snap(raw);
        setCursor(p);
        const wr = wrapRef.current?.getBoundingClientRect();
        if (wr) setTip({ x: e.clientX - wr.left, y: e.clientY - wr.top });
        if (moveRef.current) {
            const m = moveRef.current;
            const dx = p.x - m.start.x, dy = p.y - m.start.y;
            if (dx || dy) m.moved = true;
            setZones((zs) => zs.map((z) => z.id === m.id ? { ...z, coords: movedCoords(m, dx, dy) } : z));
            return;
        }
        if (drag) { applyResize(drag, p); return; }
        if (draft) setDraft((d) => ({ ...d, end: p }));
        else if (poly) setPoly((pp) => pp ? { ...pp, cursor: p } : pp);
    }

    function onPointerUp() {
        if (panRef.current) { panRef.current = null; return; }
        if (moveRef.current) { const moved = moveRef.current.moved; moveRef.current = null; setMoving(false); if (moved) setZones((zs) => { persist(zs); return zs; }); return; }
        if (drag) { setDrag(null); setZones((zs) => { persist(zs); return zs; }); return; }
        if (draft) {
            const { shape, start, end } = draft;
            if (shape === "rect") {
                const x1 = Math.min(start.x, end.x), y1 = Math.min(start.y, end.y);
                const x2 = Math.max(start.x, end.x), y2 = Math.max(start.y, end.y);
                if (Math.abs(x2 - x1) > 3 && Math.abs(y2 - y1) > 3) addZone("rectangle", [r0(x1), r0(y1), r0(x2), r0(y2)]);
            } else if (shape === "circle") {
                const rr = Math.hypot(end.x - start.x, end.y - start.y);
                if (rr > 3) addZone("circle", [r0(start.x), r0(start.y), r0(rr)]);
            }
            setDraft(null);
        }
    }

    // double-click closes a polygon
    function onDoubleClick() {
        if (poly && poly.pts.length >= 3) {
            addZone("polygon", poly.pts.flatMap((pt) => [pt.x, pt.y]));
            setPoly(null);
        }
    }

    useEffect(() => {
        function onKey(e) {
            if (e.key === "Escape") { setDraft(null); setPoly(null); setFullscreen(false); }
            if ((e.key === "Delete" || e.key === "Backspace") && selectedId && canEdit) { e.preventDefault(); deleteSelected(); }
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [selectedId, canEdit, deleteSelected]);

    // Re-fit when toggling full screen (container size changes).
    useEffect(() => { const t = setTimeout(fit, 60); return () => clearTimeout(t); }, [fullscreen, fit]);

    const TOOLS = [
        ["select", MousePointer2, "Select"], ["pan", Hand, "Pan"],
        ...(canEdit ? [["rect", Square, "Rectangle"], ["circle", CircleIcon, "Circle"], ["polygon", PenTool, "Polygon"]] : []),
    ];

    return (
        <div data-testid="asbestos-canvas" className={fullscreen ? "fixed inset-0 z-[70] bg-[#F6F4F0] p-3 flex flex-col gap-2" : "space-y-2"}>
            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-2">
                <div className="inline-flex rounded-[8px] border bg-white p-0.5" style={{ borderColor: "#EFEBE4" }}>
                    {TOOLS.map(([id, Icon, label]) => (
                        <button key={id} data-testid={`asbestos-tool-${id}`} title={label} disabled={!canEdit && id !== "select" && id !== "pan"}
                            onClick={() => { setTool(id); setDraft(null); setPoly(null); }}
                            className={`w-8 h-8 rounded-[6px] grid place-items-center disabled:opacity-40 ${tool === id ? "text-white" : "hover:bg-[#F6F4F0]"}`}
                            style={tool === id ? { background: "#C8632A" } : { color: "#6F6A64" }}>
                            <Icon className="w-4 h-4" />
                        </button>
                    ))}
                </div>

                {/* zoom */}
                <div className="inline-flex rounded-[8px] border bg-white p-0.5" style={{ borderColor: "#EFEBE4" }}>
                    <button data-testid="asbestos-zoom-out" title="Zoom out" onClick={() => setView((v) => ({ ...v, scale: Math.max(0.08, v.scale / 1.2) }))} className="w-8 h-8 rounded-[6px] grid place-items-center hover:bg-[#F6F4F0]" style={{ color: "#6F6A64" }}><ZoomOut className="w-4 h-4" /></button>
                    <button data-testid="asbestos-zoom-fit" title="Fit to view" onClick={fit} className="w-8 h-8 rounded-[6px] grid place-items-center hover:bg-[#F6F4F0]" style={{ color: "#6F6A64" }}><Maximize2 className="w-4 h-4" /></button>
                    <button data-testid="asbestos-zoom-in" title="Zoom in" onClick={() => setView((v) => ({ ...v, scale: Math.min(8, v.scale * 1.2) }))} className="w-8 h-8 rounded-[6px] grid place-items-center hover:bg-[#F6F4F0]" style={{ color: "#6F6A64" }}><ZoomIn className="w-4 h-4" /></button>
                </div>

                <button data-testid="asbestos-fullscreen" title={fullscreen ? "Exit full screen (Esc)" : "Full screen"} onClick={() => setFullscreen((f) => !f)}
                    className={`h-8 px-2.5 rounded-[8px] border text-[11.5px] inline-flex items-center gap-1.5 ${fullscreen ? "text-white" : "bg-white"}`}
                    style={fullscreen ? { background: "#C8632A", borderColor: "#C8632A" } : { borderColor: "#EFEBE4", color: "#6F6A64" }}>
                    {fullscreen ? <Shrink className="w-3.5 h-3.5" /> : <Expand className="w-3.5 h-3.5" />} {fullscreen ? "Exit" : "Full screen"}
                </button>

                {/* new-zone status */}
                {canEdit && (
                    <select data-testid="asbestos-zone-status" value={newStatus} onChange={(e) => setNewStatus(e.target.value)}
                        className="h-8 rounded-[8px] border bg-white text-[11.5px] px-2" style={{ borderColor: "#EFEBE4", color: "#1F1F1F" }}>
                        <option value="red">New zone: Asbestos present</option>
                        <option value="green">New zone: No asbestos</option>
                        <option value="unknown">New zone: Pending</option>
                    </select>
                )}

                <div className="flex-1" />

                {/* layers */}
                <div className="inline-flex items-center gap-2 rounded-[8px] border bg-white px-2.5 h-8" style={{ borderColor: "#EFEBE4" }}>
                    <Layers className="w-3.5 h-3.5" style={{ color: "#9A968F" }} />
                    {["base", "asbestos", "annotations", "guides"].map((k) => (
                        <label key={k} data-testid={`asbestos-layer-${k}`} className="text-[11px] inline-flex items-center gap-1 cursor-pointer capitalize" style={{ color: "#6F6A64" }}>
                            <input type="checkbox" checked={layers[k]} onChange={() => setLayers((l) => ({ ...l, [k]: !l[k] }))} /> {k}
                        </label>
                    ))}
                </div>

                {selectedId && canEdit && (
                    <button data-testid="asbestos-delete-zone" onClick={deleteSelected} className="h-8 px-2.5 rounded-[8px] text-[11.5px] inline-flex items-center gap-1.5 text-white" style={{ background: "#B33A2E" }}>
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                )}
            </div>

            {/* status bar + legend */}
            <div className="flex items-center justify-between text-[10.5px]" style={{ color: "#9A968F" }}>
                <span>{zones.length} zone{zones.length === 1 ? "" : "s"} · {Math.round(view.scale * 100)}%{cursor ? ` · x:${Math.round(cursor.x)} y:${Math.round(cursor.y)}` : ""}{tool === "polygon" ? " · click to add points, double-click / click first point to close, Esc to cancel" : ""}{layers.guides ? " · guides are CV-detected & approximate (not architectural geometry)" : ""}</span>
                <span className="inline-flex items-center gap-3">
                    <span className="inline-flex items-center gap-2" data-testid="asbestos-legend">
                        {[["red", "Present"], ["green", "No asbestos"], ["unknown", "Pending"]].map(([s, lbl]) => (
                            <span key={s} className="inline-flex items-center gap-1">
                                <span className="inline-grid place-items-center rounded-full text-white" style={{ width: 13, height: 13, fontSize: 9, fontWeight: 700, background: STATUS_STROKE[s] }}>{STATUS_GLYPH[s]}</span>
                                {lbl}
                            </span>
                        ))}
                    </span>
                    <span className="inline-flex items-center gap-1">
                        {saveState === "saving" && <><Loader2 className="w-3 h-3 animate-spin" /> Saving…</>}
                        {saveState === "saved" && <><Check className="w-3 h-3" style={{ color: "#2E8B3D" }} /> Saved</>}
                    </span>
                </span>
            </div>

            {/* canvas */}
            <div className={`flex gap-2 ${fullscreen ? "flex-1" : ""}`} style={{ height: fullscreen ? "auto" : "70vh" }}>
                <div ref={wrapRef} className="relative w-full rounded-[10px] overflow-hidden border select-none"
                    style={{ borderColor: "#2A2A2A", background: "#0A0A0A", height: "100%", touchAction: "none", cursor: moving ? "grabbing" : tool === "pan" ? "grab" : tool === "select" ? "default" : "crosshair" }}>
                <svg ref={svgRef} width="100%" height="100%" onWheel={onWheel}
                    onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onDoubleClick={onDoubleClick}>
                    <g transform={`translate(${view.tx} ${view.ty}) scale(${view.scale})`}>
                        {layers.base && (() => {
                            const useOriginal = !!floor.original_path;
                            const href = `${API}/asbestos/floors/${floor.id}/${useOriginal ? "original" : "standardised"}-file?t=${floor.standardised_at || ""}`;
                            return <image href={href} x={0} y={0} width={W} height={H} style={{ imageRendering: "auto" }} />;
                        })()}
                        {/* R4 — CV-detected guide lines (faint, approximate; NEVER clean geometry) */}
                        {layers.guides && (floor.guide_lines || []).map((g) => {
                            const [x1, y1, x2, y2] = g.coords;
                            return <line key={g.id} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#39B0A8" strokeOpacity="0.28"
                                strokeWidth={1 / view.scale} strokeDasharray={`${3 / view.scale} ${4 / view.scale}`} />;
                        })}
                        {layers.asbestos && zones.map((z, zi) => (
                            <ZoneShape key={z.id} z={z} testId={`asbestos-zone-${zi}`} selected={z.id === selectedId} hovered={z.id === hoveredId} invScale={1 / view.scale}
                                cursor={tool === "select" && canEdit ? (z.id === selectedId ? "move" : "pointer") : "pointer"}
                                onClick={() => tool === "select" && setSelectedId(z.id)}
                                onPointerDownShape={(e) => startMove(e, z)}
                                onEnter={() => setHoveredId(z.id)} onLeave={() => setHoveredId((h) => h === z.id ? null : h)} />
                        ))}
                        {/* non-colour status glyphs (colour-blind / mono-print safe) */}
                        {layers.asbestos && zones.map((z) => {
                            const c = zoneCentroid(z); const r = 8 / view.scale;
                            return (
                                <g key={`g_${z.id}`} pointerEvents="none">
                                    <circle cx={c.x} cy={c.y} r={r} fill={STATUS_STROKE[z.status]} stroke="#fff" strokeWidth={1.2 / view.scale} />
                                    <text x={c.x} y={c.y + r * 0.34} textAnchor="middle" fill="#fff" fontWeight="700"
                                        fontSize={11 / view.scale} fontFamily="Arial">{STATUS_GLYPH[z.status] || "?"}</text>
                                </g>
                            );
                        })}
                        {/* resize handles for the selected zone (edit size) */}
                        {canEdit && tool === "select" && selectedZone && layers.asbestos && (
                            <ResizeHandles z={selectedZone} invScale={1 / view.scale}
                                onGrab={(kind, index) => setDrag({ id: selectedZone.id, kind, index })} />
                        )}
                        {/* draft preview */}
                        {draft && draft.shape === "rect" && (
                            <rect x={Math.min(draft.start.x, draft.end.x)} y={Math.min(draft.start.y, draft.end.y)}
                                width={Math.abs(draft.end.x - draft.start.x)} height={Math.abs(draft.end.y - draft.start.y)}
                                fill={STATUS_FILL[newStatus]} stroke={STATUS_STROKE[newStatus]} strokeWidth={2 / view.scale} strokeDasharray={`${4 / view.scale} ${3 / view.scale}`} />
                        )}
                        {draft && draft.shape === "circle" && (
                            <circle cx={draft.start.x} cy={draft.start.y} r={Math.hypot(draft.end.x - draft.start.x, draft.end.y - draft.start.y)}
                                fill={STATUS_FILL[newStatus]} stroke={STATUS_STROKE[newStatus]} strokeWidth={2 / view.scale} strokeDasharray={`${4 / view.scale} ${3 / view.scale}`} />
                        )}
                        {poly && (
                            <polyline points={[...poly.pts, poly.cursor].map((p) => `${p.x},${p.y}`).join(" ")}
                                fill="none" stroke={STATUS_STROKE[newStatus]} strokeWidth={2 / view.scale} strokeDasharray={`${4 / view.scale} ${3 / view.scale}`} />
                        )}
                        {poly && poly.pts.map((p, i) => (
                            <circle key={i} cx={p.x} cy={p.y} r={4 / view.scale} fill={STATUS_STROKE[newStatus]} />
                        ))}
                        {/* snap cursor marker */}
                        {cursor && cursor.snapped && <circle cx={cursor.x} cy={cursor.y} r={6 / view.scale} fill="none" stroke="#1F6FE5" strokeWidth={2 / view.scale} />}
                    </g>
                </svg>

                {/* hover tooltip */}
                {layers.asbestos && hoveredZone && tip && hoveredId !== selectedId && (
                    <div data-testid="asbestos-tooltip" className="pointer-events-none absolute z-20 rounded-[8px] shadow-lg px-3 py-2 text-[11.5px] max-w-[240px]"
                        style={{ left: Math.min(tip.x + 14, (wrapRef.current?.clientWidth || 600) - 230), top: tip.y + 14, background: "rgba(20,20,20,0.94)", color: "#fff", border: `1px solid ${STATUS_STROKE[hoveredZone.status]}` }}>
                        <div className="flex items-center gap-1.5 font-semibold">
                            <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: STATUS_STROKE[hoveredZone.status] }} />
                            {hoveredZone.label || "(unlabelled zone)"}
                        </div>
                        <div className="opacity-80 mt-0.5">{STATUS_LABEL[hoveredZone.status] || hoveredZone.status}{hoveredZone.reference_id ? ` · Ref ${hoveredZone.reference_id}` : ""}</div>
                        {hoveredZone.hover_text && <div className="opacity-70 mt-0.5">{hoveredZone.hover_text}</div>}
                        {hoveredZone.linked_document_ids?.length > 0 && <div className="opacity-70 mt-0.5 inline-flex items-center gap-1"><FileText className="w-3 h-3" /> {hoveredZone.linked_document_ids.length} document{hoveredZone.linked_document_ids.length === 1 ? "" : "s"}</div>}
                    </div>
                )}

                {/* click detail panel */}
                {selectedZone && (
                    <ZoneDetailPanel zone={selectedZone} canEdit={canEdit} documents={documents}
                        buildingId={buildingId} clientId={clientId}
                        onChange={(patch) => updateZone(selectedZone.id, patch)}
                        onLocalChange={(patch) => updateZoneLocal(selectedZone.id, patch)}
                        onDelete={deleteSelected} onClose={() => setSelectedId(null)}
                        onDocsRefresh={onDocsRefresh}
                        onOpenDoc={(docId) => navigate(`/app/documents?open=${docId}&back=${encodeURIComponent(location.pathname)}`)} />
                )}
                </div>
            </div>
        </div>
    );
}
